import { FC, useEffect, useRef, useState } from 'react'
import { CmsAsset } from '../Sections/PageHero'
import { CONTENT_TYPE_MP4, VideoAsset } from '../Blog/BlogPost/VideoAsset'
import { BodySize } from '../constants'
import throttle from 'lodash/throttle'
import playImg from '@/assets/icons/play.svg'
import { getContentfulAssetUrl } from '@/utils/contentfulAsset'
import styles from './styles.module.scss'
import { IconographyButton } from '@/components/IconographyButton/IconographyButton'

export interface VideoProps {
  video: CmsAsset
  videoPoster?: CmsAsset
  mobileVideo?: CmsAsset
  mobileVideoPoster?: CmsAsset
  play?: boolean
  className?: string
  onPlay?: () => void
}

export const Video: FC<VideoProps> = ({
  video,
  videoPoster,
  mobileVideo,
  mobileVideoPoster,
  play = false,
  className,
  onPlay = () => {},
}) => {
  const videoRef = useRef<HTMLDivElement>(null)
  const [videoPlayed, setVideoPlayed] = useState(false)
  const [width, setWidth] = useState(0)
  const [duration, setDuration] = useState('')

  const updateDuration = () => {
    if (videoRef.current) {
      const video = videoRef.current.querySelector('video')
      const duration = video?.duration || 0
      const minutes = Math.floor(duration / 60)
      const remainingSeconds = Math.ceil(duration % 60)

      setDuration(
        `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`,
      )
    }
  }

  const handleResize = () => {
    setWidth(window.innerWidth)
  }

  useEffect(() => {
    const callback = throttle(handleResize, 100)

    window.addEventListener('resize', callback)

    return () => window.removeEventListener('resize', callback)
  }, [width])

  useEffect(() => {
    setWidth(window.innerWidth)
  }, [])

  const handlePlayVideo = async () => {
    if (videoRef.current && !videoPlayed) {
      const video = videoRef.current.querySelector('video')

      await video?.play()

      setVideoPlayed(true)
    }
  }

  const pauseVideo = async () => {
    if (videoRef.current) {
      const video = videoRef.current.querySelector('video')

      video?.pause()
    }
  }

  useEffect(() => {
    if (play) {
      handlePlayVideo()
    } else {
      pauseVideo()
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [play])

  const getDynamicUrl = () => {
    if (width > BodySize.maxMobile) {
      return getContentfulAssetUrl(video?.fields.file.url)
    } else {
      return mobileVideo
        ? getContentfulAssetUrl(mobileVideo.fields.file.url)
        : getContentfulAssetUrl(video?.fields.file.url)
    }
  }

  const getDynamicPoster = () => {
    if (width > BodySize.maxMobile) {
      return getContentfulAssetUrl(videoPoster?.fields.file.url)
    } else {
      return mobileVideoPoster
        ? getContentfulAssetUrl(mobileVideoPoster.fields.file.url)
        : getContentfulAssetUrl(videoPoster?.fields.file.url)
    }
  }

  return (
    <div className={styles.video}>
      <div ref={videoRef}>
        <VideoAsset
          onPlay={onPlay}
          className={className}
          onLoad={updateDuration}
          controls={videoPlayed}
          url={getDynamicUrl() || ''}
          poster={getDynamicPoster()}
          fileName={video.fields.file.fileName!}
          videoType={CONTENT_TYPE_MP4}
        />
      </div>

      {!videoPlayed && (
        <div className={styles.cta}>
          <IconographyButton
            icon={playImg}
            value="Play"
            disabled={false}
            handler={handlePlayVideo}
          />
        </div>
      )}

      {!videoPlayed && duration && (
        <div data-testid="duration" className={styles.duration}>
          {duration}
        </div>
      )}
    </div>
  )
}
